/**
 * Determines whether a product contains the data required for style grouping.
 *
 * @param {Object} product - Product to inspect.
 * @returns {boolean} Whether style-grouping behaviour should be enabled.
 */
export const isStyleGroupingActive = (product) =>
    Boolean(product?.c_associatedVariationGroupIds && product?.c_associatedVariationAttributes)

/**
 * Resolves the associated product ID represented by a variation value.
 *
 * @param {Object} product - Initial style-grouped product.
 * @param {string} attributeId - Variation attribute ID.
 * @param {Object} value - Variation value.
 * @returns {string|undefined} Associated product ID for a colour value.
 */
export const getStyleGroupingTargetProductId = (product, attributeId, value) => {
    if (attributeId !== 'color' || !isStyleGroupingActive(product)) {
        return undefined
    }

    return value?.associatedPID || value?.productId
}

/**
 * Returns all product IDs represented by the grouped colour values.
 *
 * @param {Object} product - Initial style-grouped product.
 * @returns {string[]} Associated product IDs.
 */
export const getStyleGroupingProductIds = (product) =>
    (product?.c_associatedVariationAttributes?.find(({id}) => id === 'color')?.values || [])
        .map(({associatedPID, productId}) => associatedPID || productId)
        .filter(Boolean)

/**
 * Resolves the selected style-group product from the current query string.
 *
 * @param {Object} product - Initial style-grouped product.
 * @param {string} search - Current URL query string.
 * @returns {string|undefined} Selected style-group product ID.
 */
export const getSelectedStyleGroupingProductId = (product, search = '') => {
    if (!isStyleGroupingActive(product)) {
        return undefined
    }

    const selectedPid = new URLSearchParams(search).get('pid')
    const associatedProductIds = getStyleGroupingProductIds(product)

    return associatedProductIds.includes(selectedPid)
        ? selectedPid
        : product?.id || product?.productId
}

/**
 * Adds or replaces the product ID in a variation href.
 *
 * @param {string} href - Variation href.
 * @param {string} targetProductId - Product ID to store in the href.
 * @returns {string} Updated href.
 */
export const addPidToHref = (href, targetProductId) => {
    if (!href || !targetProductId) {
        return href
    }

    const hashIndex = href.indexOf('#')
    const hash = hashIndex >= 0 ? href.slice(hashIndex) : ''
    const hrefWithoutHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href
    const [path, queryString = ''] = hrefWithoutHash.split('?')
    const params = new URLSearchParams(queryString)
    params.set('pid', targetProductId)

    return `${path}?${params.toString()}${hash}`
}

/**
 * Decorates modal variation attributes with style-group product identity.
 *
 * @param {Object} options - Decoration options.
 * @param {Array} options.variationAttributes - Derived variation attributes.
 * @param {Object} options.initialProduct - Initial style-grouped product.
 * @param {string} options.selectedStyleGroupingProductId - Selected grouped product ID.
 * @returns {Array} Variation attributes ready for modal rendering.
 */
export const decorateModalVariationAttributes = ({
    variationAttributes = [],
    initialProduct,
    selectedStyleGroupingProductId
}) => {
    if (!isStyleGroupingActive(initialProduct)) {
        return variationAttributes
    }

    return variationAttributes.map((attribute) => {
        if (attribute.id !== 'color') {
            return {
                ...attribute,
                values: attribute.values?.map((value) => ({
                    ...value,
                    href: addPidToHref(value.href, selectedStyleGroupingProductId)
                }))
            }
        }

        const selectedValue =
            attribute.values?.find(
                (value) =>
                    value.value === attribute.selectedValue?.value &&
                    getStyleGroupingTargetProductId(initialProduct, attribute.id, value) ===
                        selectedStyleGroupingProductId
            ) || attribute.values?.find((value) => value.value === attribute.selectedValue?.value)

        return {
            ...attribute,
            selectedValue: selectedValue
                ? {
                      ...attribute.selectedValue,
                      name: selectedValue.name,
                      value: selectedValue.value,
                      targetProductId: getStyleGroupingTargetProductId(
                          initialProduct,
                          attribute.id,
                          selectedValue
                      )
                  }
                : attribute.selectedValue,
            values: attribute.values?.map((value) => {
                const targetProductId = getStyleGroupingTargetProductId(
                    initialProduct,
                    attribute.id,
                    value
                )

                return {
                    ...value,
                    href: addPidToHref(value.href, targetProductId)
                }
            })
        }
    })
}
